import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getRef } from "@/lib/referral";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function startGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthModal({ open, onOpenChange, defaultTab = "login" }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = async (path, body) => {
    setErr(""); setLoading(true);
    try {
      const res = await api.post(path, body);
      setUser(res.data.user);
      onOpenChange(false);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "Please try again.");
    } finally { setLoading(false); }
  };

  const submitLogin = (e) => { e.preventDefault(); go("/auth/login", { email: form.email, password: form.password }); };
  const submitRegister = (e) => { e.preventDefault(); go("/auth/register", { name: form.name, email: form.email, password: form.password }); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="auth-modal">
        <DialogHeader><DialogTitle>Welcome to Syllabify</DialogTitle></DialogHeader>

        <Button onClick={startGoogle} variant="outline" data-testid="google-auth-btn"
          className="w-full rounded-lg border-slate-300">
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="h-px flex-1 bg-slate-200" />
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setErr(""); }}>
          <TabsList className="w-full">
            <TabsTrigger value="login" className="flex-1" data-testid="tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="register" className="flex-1" data-testid="tab-register">Create account</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={submitLogin} className="space-y-3 mt-3">
              <div className="relative"><Mail className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <Input type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="login-email" className="pl-9" required /></div>
              <div className="relative"><Lock className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <Input type="password" placeholder="Password" value={form.password} onChange={set("password")} data-testid="login-password" className="pl-9" required /></div>
              {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={submitRegister} className="space-y-3 mt-3">
              <div className="relative"><UserIcon className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <Input placeholder="Full name" value={form.name} onChange={set("name")} data-testid="register-name" className="pl-9" required /></div>
              <div className="relative"><Mail className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <Input type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="register-email" className="pl-9" required /></div>
              <div className="relative"><Lock className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <Input type="password" placeholder="Password (min 8 characters)" value={form.password} onChange={set("password")} data-testid="register-password" className="pl-9" required /></div>
              {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
              <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
